import { useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@sportninja/common/config/queryKeys'
import {
  sendInGroups,
  type AvailabilityRequestGroup,
  type AvailabilityRequestGroupOutcome,
} from '@sportninja/common/features/playerFinder/sendGroups'
import { sendAvailabilityRequests } from '@sportninja/common/services/game/spareCandidatesService'

export interface SendAvailabilityRequestGroupsArgs {
  gameId: string
  teamId: string
  groups: AvailabilityRequestGroup[]
  // Optional free-text note sent with every group's request.
  message?: string
}

/**
 * SN-8309 — one POST per candidate source, one after another (a POST files
 * every ask under one source). Resolves with each group's outcome instead of
 * failing as a whole: the asks that went out stay sent, and only the failed
 * groups need a retry.
 */
export const useSendAvailabilityRequest = () => {
  const queryClient = useQueryClient()
  return useMutation<
    AvailabilityRequestGroupOutcome[],
    Error,
    SendAvailabilityRequestGroupsArgs
  >({
    mutationFn: ({ gameId, teamId, groups, message }) =>
      sendInGroups(groups, (group) =>
        sendAvailabilityRequests({
          gameId,
          teamId,
          playerIds: group.playerIds,
          source: group.source,
          message,
        })
      ),
    retry: 0,
    onSuccess: () => {
      // After every send, even one where every group failed: the asks that went
      // out show, and so does anything the server recorded for a group whose
      // answer never arrived (cut off by the time limit, or the connection
      // dropped).
      queryClient.invalidateQueries({
        queryKey: queryKeys.spareCandidates.all,
      })
      // The Attendance roster too: an ask lists the player among its spare
      // candidates.
      queryClient.invalidateQueries({
        queryKey: queryKeys.gameRosterPlayers.all,
      })
    },
  })
}
